/**
 * Analyzes a sky image for its average brightness (0-1).
 *
 * Draws the image into an offscreen canvas, samples pixel data, and computes
 * a perceived-luminance average using the ITU-R BT.709 weighting formula:
 *   L = 0.2126 * R + 0.7152 * G + 0.0722 * B
 *
 * A `sampleStep` greater than 1 skips pixels so large images are analyzed
 * without reading every single pixel.
 *
 * @returns A number between 0 (fully dark) and 1 (fully bright).
 */

export type AnalyzeImageBrightnessParams = {
	/** The data-URL string of the uploaded image */
	dataUrl: string;
	/** Pixel skip factor — higher values are faster but less precise (default 4) */
	sampleStep?: number;
};

export const analyzeImageBrightness = (params: AnalyzeImageBrightnessParams): Promise<number> => {
	const { dataUrl, sampleStep = 4 } = params;

	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'anonymous';

		img.onload = (): void => {
			try {
				// Cap the analysis canvas at 512px to keep it fast
				const maxDim = 512;
				const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
				const width = Math.round(img.width * scale);
				const height = Math.round(img.height * scale);

				const canvas = document.createElement('canvas');
				canvas.width = width;
				canvas.height = height;

				const ctx = canvas.getContext('2d');
				if (!ctx) {
					resolve(0.5); // fallback to mid-brightness
					return;
				}

				ctx.drawImage(img, 0, 0, width, height);
				const { data } = ctx.getImageData(0, 0, width, height);

				let totalLuminance = 0;
				let sampleCount = 0;
				const step = Math.max(1, Math.round(sampleStep));

				for (let i = 0; i < data.length; i += 4 * step) {
					const r = data[i] / 255;
					const g = data[i + 1] / 255;
					const b = data[i + 2] / 255;

					// ITU-R BT.709 perceived luminance
					totalLuminance += 0.2126 * r + 0.7152 * g + 0.0722 * b;
					sampleCount++;
				}

				const averageBrightness = sampleCount > 0 ? totalLuminance / sampleCount : 0.5;
				resolve(Math.max(0, Math.min(1, averageBrightness)));
			} catch (_error) {
				// On any canvas/security error, return neutral brightness
				resolve(0.5);
			}
		};

		img.onerror = (): void => {
			reject(new Error('Failed to load image for brightness analysis'));
		};

		img.src = dataUrl;
	});
};
